import type { Map } from "@iventis/mapbox-gl";
import type { Feature, LineString } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types/src/map-object-properties";
import type { BehaviorSubject } from "rxjs";
import type { LineModelStyle } from "@iventis/domain-model/model/lineModelStyle";
import type { ModelDataStore } from "../../data-store/model-data-store";
import type { DataFieldListItemStore } from "../../data-store/data-field-list-item-store";
import type { MapModuleLayer, MapState } from "../../types/store-schema";
import { MapboxModelCustomisableLayer } from "./mapbox-model-customisable-layer";
import { createFeaturesOnLine } from "../mapbox/3d-engine/3d-engine-helpers";
import { MapboxLineModelGuideLineLayer } from "./mapbox-line-model-guide-line-layer";
import type { StylePropertyToValueMapV2 } from "../../types/style-types";

export class MapboxLineModelLayer extends MapboxModelCustomisableLayer<Feature<LineString, MapObjectProperties>[], LineModelStyle> {
    private guideLineLayer: MapboxLineModelGuideLineLayer;

    private readonly styleChangesAffectingPositioning: (keyof LineModelStyle)[] = ["rotation", "spacing", "modelOffset"];

    constructor(
        map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        dataFieldListItemDataStore: DataFieldListItemStore,
        events: {
            updateGuideLine: (callback: (show: boolean, mapObjectIds: string[]) => void, layerId: string) => void;
            getAboveLayerId: (layerId: string) => string;
        }
    ) {
        super(map, layer, state$, modelDataStore, dataFieldListItemDataStore, events);
        events.updateGuideLine(this.updateGuideLine.bind(this), this.layerId);
    }

    public async initaliseLayer(layer: MapModuleLayer): Promise<void> {
        await super.initaliseLayer(layer);
        this.guideLineLayer = new MapboxLineModelGuideLineLayer(this.state$, this.layerId, this.map);
    }

    public updateMapOrder(aboveLayerId: string): void {
        super.updateMapOrder(aboveLayerId);
        this.guideLineLayer.updateMapOrder();
    }

    public updateGuideLine(show: boolean, mapObjectIds: string[]) {
        if (show) {
            this.guideLineLayer?.showGuideLinesFor(mapObjectIds);
        } else {
            this.guideLineLayer?.removeGuideLines();
        }
    }

    public removeLayer() {
        super.removeLayer();
        this.guideLineLayer?.removeLayer();
        this.guideLineLayer?.removeLocalSource();
    }

    public updateLocalSource(source: Feature<LineString, MapObjectProperties>[]) {
        const layer = this.state$.value.layers.value.find((layer) => layer.id === this.layerId);
        const features = createFeaturesOnLine(this.layerId, source, layer.lineModelStyle.spacing, layer.lineModelStyle.rotation, layer.lineModelStyle.modelOffset);
        let layerSource = this.map.getSource(this.sourceId);

        if (layerSource == null) {
            this.addLocalSource();
            layerSource = this.map.getSource(this.sourceId);
        }

        if (layerSource != null && layerSource.type === "geojson") {
            layerSource.setData(features);
        }

        this.guideLineLayer?.updateLocalSource(source);
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<LineModelStyle>): Promise<void> {
        // Spilt the changes into two groups, one for this class to deal with and another for MapboxModelClass to deal with
        const modelStyleChanges = Object.entries(styleChanges).reduce<StylePropertyToValueMapV2<LineModelStyle>>(
            (acc, [styleProperty, value]) =>
                !this.styleChangesAffectingPositioning.some((positionalChange) => positionalChange === styleProperty) ? { ...acc, [styleProperty]: value } : acc,
            {}
        );
        const lineModelStyleChanges = Object.entries(styleChanges).reduce<StylePropertyToValueMapV2<LineModelStyle>>(
            (acc, [styleProperty, value]) =>
                this.styleChangesAffectingPositioning.some((positionalChange) => positionalChange === styleProperty) ? { ...acc, [styleProperty]: value } : acc,
            {}
        );

        await super.updateStyle(modelStyleChanges);

        if (lineModelStyleChanges.spacing || lineModelStyleChanges.modelOffset || lineModelStyleChanges.rotation) {
            // All of the above styles change the positioning of the models along the lines, so just redraw them
            const geojson = this.state$.value.geoJSON.value[this.layerId]?.map(({ feature }) => feature as Feature<LineString, MapObjectProperties>);
            this.updateLocalSource(geojson);
        }
    }

    public getLayerIdsForSnapping(): string[] {
        // For  a line of 3d models we only want to snap with the guide line
        return this.guideLineLayer.getLayerIdsForSnapping();
    }
}
